import { BillingPeriod, PaymentPlan, Policy, PolicyLayer, User } from "@deathstar/types/northstar";
import { PaymentIntentMetadata } from "@deathstar/types/stripe";
import { DateTime, NumberFormatter, capitalizeFirst } from "@deathstar/types/util";
import { CheckCircleIcon, Square2StackIcon } from "@heroicons/react/24/outline";
import { Accordion, AccordionDetails, AccordionSummary, Dialog } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Skeleton } from "@material-ui/lab";
import React from "react";
import { classNames } from "../classNames/classNames";
import { useSnackbar } from "../hooks/useSnackbar";
import { PaymentForm } from "../paymentForm/paymentForm";
import { PrimaryButton } from "../primaryButton/primaryButton";
import { ThreeDotMenu } from "../threeDotMenu/threeDotMenu";
import { Tooltip } from "../tooltip/tooltip";
import { Context, IContext } from "./context";
import { StatementDialog } from "./statementDialog";

const TRANSVERSE_WRITING_COMPANY_NAME = "MS Transverse Insurance Company";

function shouldShowDueDates(policy: { writingCompany?: { name?: string } }): boolean {
    return policy.writingCompany?.name !== TRANSVERSE_WRITING_COMPANY_NAME;
}

export function BillingPeriodList({
    policy,
    user,
    isLoading,
    onPaymentSuccess,
}: {
    policy: Policy;
    onPaymentSuccess: IContext["onPaymentSuccess"];
    isLoading: boolean;
    user?: User;
}): JSX.Element {
    if (isLoading) {
        return (
            <ul className="flex flex-col gap-2">
                {policy.billing?.billingPeriods.map(() => {
                    return (
                        <li>
                            <Skeleton className="h-[50px] scale-100" />
                        </li>
                    );
                })}
            </ul>
        );
    }

    if (
        policy.billMethod === Policy.BillMethod.DIRECT ||
        policy.paymentPlanId === PaymentPlan.Id.PREMIUM_FINANCE ||
        policy.paymentPlanId === PaymentPlan.Id.OTHER
    ) {
        return <MinimalList policy={policy} user={user} />;
    }

    return <FullList policy={policy} user={user} onPaymentSuccess={onPaymentSuccess} />;
}

function MinimalList({ policy, user }: { policy: Policy; user?: User }): JSX.Element {
    const showDueDates = shouldShowDueDates(policy);
    const data = React.useMemo(() => {
        const all: {
            id: string;
            chargeId?: string;
            number: string;
            charged: string;
            note: string;
            paid: string;
            dueDate: string;
            reconciled: boolean;
        }[] = [];

        policy.billing!.billingPeriods.forEach((bp) => {
            bp.getMergedChargesAndInvoices().map((m) => {
                return {
                    id: m.id,
                    chargeId: m.chargeId,
                    number: m.invoiceNumber,
                    charged: NumberFormatter.Currency.format(m.charged || 0),
                    paid: NumberFormatter.Currency.format(m.paid || 0),
                    dueDate: m.dueDate ? new DateTime(m.dueDate).format("Mmmm dd, YYYY") : "none",
                    reconciled: m.reconciled,
                };
            });
        });

        return all;
    }, [policy.billing]);

    return (
        <div className="flex w-full flex-col gap-2">
            {data.map((d, i) => (
                <div
                    key={i}
                    className={classNames("grid items-center gap-4", {
                        "grid-cols-[max-content_1fr_max-content_min-content]": showDueDates,
                        "grid-cols-[max-content_1fr_min-content]": !showDueDates,
                    })}
                >
                    <Tooltip title={d.chargeId ? `Stripe ID: ${d.chargeId}` : ""} underline>
                        <p className="font-mono">#{d.number}</p>
                    </Tooltip>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <p className="">
                                Charged: {d.charged} / Paid: {d.paid}
                            </p>
                            {d.reconciled && (
                                <Tooltip title="Reconciled" underline="none">
                                    <CheckCircleIcon className="relative top-[-1px] h-4 w-4 text-green-500" />
                                </Tooltip>
                            )}
                        </div>
                        <SpecialPaymentAttribute policy={policy} invoiceId={d.id} />
                    </div>

                    {showDueDates && <p className="text-right">{d.dueDate}</p>}

                    {user?.isDeveloper && (
                        <ThreeDotMenu
                            options={[
                                {
                                    id: "copyInvoiceId",
                                    label: "Copy Invoice ID",
                                    onClick: () => {
                                        if (!d.chargeId) return;

                                        navigator.clipboard
                                            .writeText(d.number)
                                            .then(() => {
                                                useSnackbar.add("Copied to clipboard", { variant: "success" });
                                            })
                                            .catch(() => {
                                                useSnackbar.add("Could not copy. Please try again.", {
                                                    variant: "warning",
                                                });
                                            });
                                    },
                                    hidden: isNaN(parseInt(d.number)),
                                },
                                {
                                    id: "copyStripeId",
                                    label: "Copy Stripe ID",
                                    onClick: () => {
                                        if (!d.chargeId) return;

                                        navigator.clipboard
                                            .writeText(d.chargeId)
                                            .then(() => {
                                                useSnackbar.add("Copied to clipboard", { variant: "success" });
                                            })
                                            .catch(() => {
                                                useSnackbar.add("Could not copy. Please try again.", {
                                                    variant: "warning",
                                                });
                                            });
                                    },
                                    hidden: !d.chargeId,
                                },
                            ]}
                        ></ThreeDotMenu>
                    )}
                </div>
            ))}
        </div>
    );
}

function FullList({
    policy,
    user,
    onPaymentSuccess,
}: {
    policy: Policy;
    user?: User;
    onPaymentSuccess: IContext["onPaymentSuccess"];
}): JSX.Element {
    const ctx = React.useContext(Context);
    const showDueDates = shouldShowDueDates(policy);
    const enableActions = React.useMemo(() => {
        if (ctx.enableActions === false) {
            return {
                payNow: false,
                reportNow: false,
                viewStatement: false,
                viewBillingPeriodDetails: false,
            };
        }
        if (ctx.enableActions === true) {
            return {
                payNow: true,
                reportNow: true,
                viewStatement: true,
                viewBillingPeriodDetails: true,
            };
        }
        return ctx.enableActions;
    }, [ctx.enableActions]);

    return (
        <div className="overflow-scroll">
            <ul>
                <li
                    className={classNames("grid gap-2 py-1", {
                        "grid-cols-1": !ctx.enableActions,
                        "grid-cols-[11fr_2fr]": ctx.enableActions,
                    })}
                >
                    <Accordion elevation={0} className="pointer-events-none">
                        <AccordionSummary>
                            <div
                                className={classNames("grid w-full gap-4 text-lg text-gray-500", {
                                    "grid-cols-4": showDueDates,
                                    "grid-cols-3": !showDueDates,
                                })}
                            >
                                <p>Premium</p>
                                <p>Amount Due</p>
                                <p>Coverage Dates</p>
                                {showDueDates && <p className="pr-8 text-right">Due Date</p>}
                            </div>
                        </AccordionSummary>
                    </Accordion>
                </li>
                {policy.billing?.billingPeriods.map((period, i) => {
                    return (
                        <ListItem
                            key={i}
                            policy={policy}
                            period={period}
                            user={user}
                            enableActions={enableActions}
                            onPaymentSuccess={onPaymentSuccess}
                        />
                    );
                })}
            </ul>
        </div>
    );
}

function ListItem({
    policy,
    period,
    user,
    enableActions,
    onPaymentSuccess,
}: {
    policy: Policy;
    period: BillingPeriod;
    user?: User;
    enableActions: { payNow: boolean; reportNow: boolean; viewStatement: boolean; viewBillingPeriodDetails: boolean };
    onPaymentSuccess: IContext["onPaymentSuccess"];
}): JSX.Element {
    const charged = React.useMemo(() => period.getCharged(), [period]);
    const due = React.useMemo(() => period.getDue(), [period]);
    const showDueDates = shouldShowDueDates(policy);

    const report = React.useMemo(() => {
        if (!Policy.isComposite(policy) && policy.reports?.length) return null;
        return (
            policy.reports?.find(
                (report) => report.month === period.endDate.getMonthActual() && report.year === period.endDate.getFullYear()
            ) || null
        );
    }, [policy, period]);

    return (
        <li
            className={classNames("grid gap-2 py-1", {
                "grid-cols-1": !enableActions,
                "grid-cols-[11fr_2fr]": enableActions,
            })}
        >
            <Accordion
                elevation={0}
                className={classNames("border-dashed border-red-400", {
                    border: period.isFirst && policy.billing?.specialPayment?.isPaid === false,
                })}
            >
                <AccordionSummary
                    className={classNames({
                        "pointer-events-none": !enableActions.viewBillingPeriodDetails,
                        "bg-gray-100":
                            period.isFuture ||
                            (period.isCurrent && charged.projected) ||
                            (period.isPast && !period.isOverdue && period.projected),
                        "bg-green-100": period.isPaid && period.charged > 0,
                        "bg-red-300": period.isOverdue,
                        "bg-blue-100": period.isCurrent && !period.projected,
                        "bg-orange-100": period.isPast && period.isNonBilled && policy.billMethod === Policy.BillMethod.AGENCY,
                    })}
                    expandIcon={enableActions.viewBillingPeriodDetails && <ExpandMoreIcon />}
                >
                    <div
                        className={classNames("grid w-full gap-4", {
                            "grid-cols-4": showDueDates,
                            "grid-cols-3": !showDueDates,
                        })}
                    >
                        <div className="flex items-center gap-2">
                            <p>
                                {NumberFormatter.Currency.format(charged.amount)}
                                {charged.projected && charged.amount > 0 && "*"}
                            </p>
                            {user?.isDeveloper && (
                                <Tooltip title="Copy to clipboard" underline="none">
                                    <button
                                        className="rounded-full text-navigator-blue hover:bg-black/10 active:bg-black/20"
                                        onClick={() => {
                                            navigator.clipboard
                                                .writeText(charged.amount.toString())
                                                .then(() => {
                                                    useSnackbar.add("Copied amount to clipboard", { variant: "success" });
                                                })
                                                .catch(() => {
                                                    useSnackbar.add("Could not copy. Please try again.", {
                                                        variant: "warning",
                                                    });
                                                });
                                        }}
                                    >
                                        <Square2StackIcon className="h-4 w-4" />
                                    </button>
                                </Tooltip>
                            )}
                        </div>
                        <p>
                            {NumberFormatter.Currency.format(due.amount)}
                            {due.projected && due.amount > 0 && "*"}
                        </p>
                        <p className="">
                            {period.startDate.format("mm/dd/YY")} - {period.endDate.format("mm/dd/YY")}
                        </p>
                        {showDueDates && <PayPlanDueDate policy={policy} BillingPeriod={period} />}
                    </div>
                </AccordionSummary>
                <AccordionDetails>
                    <InvoiceList policy={policy} billingPeriod={period} user={user} />
                </AccordionDetails>
            </Accordion>
            {enableActions && (
                <div className="flex gap-2 pt-2">
                    <Menu
                        policy={policy}
                        period={period}
                        report={report}
                        onPaymentSuccess={onPaymentSuccess}
                        enableActions={enableActions}
                    />
                    <Actions
                        policy={policy}
                        period={period}
                        report={report}
                        onPaymentSuccess={onPaymentSuccess}
                        enableActions={enableActions}
                    />
                </div>
            )}
        </li>
    );
}

interface IActionsOptions {
    policy: Policy;
    period: BillingPeriod;
    report?: Policy.IReport | null;
    reportNowButtonEnabled?: boolean;
    payNowButtonEnabled?: boolean;
    enableActions: { payNow: boolean; reportNow: boolean; viewStatement: boolean; viewBillingPeriodDetails: boolean };
    onPaymentSuccess: IContext["onPaymentSuccess"];
}

function Actions(options: IActionsOptions): JSX.Element | null {
    const { period, report, enableActions } = options;

    const payNowEnabled = React.useMemo(() => period.totalDue > 0 || period.isCurrent, [period.isCurrent, period.totalDue]);
    const { reportNowMenuOptionEnabled: reportNowButtonEnabled } = useReportActions(options);
    const payNowButtonEnabled = React.useMemo(() => {
        if (!enableActions.payNow) return false;
        if (!period.hasNonProjectedBalanceDue) return false;
        if (period.schedule.isComposite && !report?.isSubmitted && !report?.isFinalized) {
            return false;
        }
        return true;
    }, [enableActions.payNow, period.hasNonProjectedBalanceDue, period.schedule.isComposite, report]);
    const o = React.useMemo(() => {
        return {
            ...options,
            payNowEnabled,
            payNowButtonEnabled,
            reportNowButtonEnabled,
        };
    }, [options, payNowButtonEnabled, payNowEnabled, reportNowButtonEnabled]);

    return (
        <div className="flex flex-1 flex-col gap-2">
            {reportNowButtonEnabled && <ReportNowButton {...o} />}
            {payNowButtonEnabled && <PayNowButton {...o} />}
        </div>
    );
}

function PayNowButton({ policy, period, onPaymentSuccess }: IActionsOptions): JSX.Element | null {
    const ctx = React.useContext(Context);
    const [isPaymentFormOpen, setIsPaymentFormOpen] = React.useState(false);

    return (
        <>
            <PrimaryButton
                className={classNames("h-min bg-navigator-primary", {})}
                onClick={() => {
                    setIsPaymentFormOpen(true);
                }}
                disabled={!ctx.billingInfo || !ctx.createPayment}
            >
                Pay
            </PrimaryButton>
            {ctx.billingInfo && ctx.createPayment && (
                <PaymentFormDialog
                    open={isPaymentFormOpen}
                    onClose={() => setIsPaymentFormOpen(false)}
                    policy={policy}
                    period={period}
                    billingInfo={ctx.billingInfo}
                    createPayment={ctx.createPayment}
                    isLoadingBillingInfo={ctx.isLoadingBillingInfo ?? false}
                    onPaymentSuccess={onPaymentSuccess}
                />
            )}
        </>
    );
}

function useNavigateToReporting(policy: Policy, period: BillingPeriod) {
    const ctx = React.useContext(Context);
    const year = React.useMemo(() => period.endDate.getFullYear(), [period.endDate]);
    const month = React.useMemo(() => period.endDate.getMonthActual(), [period.endDate]);
    return React.useCallback(() => {
        if (Policy.isCompositeDoubleAdvanced(policy) && period.index === 0) {
            ctx.navigateToReporting?.({
                policyId: policy.id,
            });
        } else {
            ctx.navigateToReporting?.({
                policyId: policy.id,
                year,
                month,
                report: period.report,
            });
        }
    }, [ctx, month, period.index, period.report, policy, year]);
}

function useReportActions({ enableActions, policy, period, report }: IActionsOptions) {
    const navigate = useNavigateToReporting(policy, period);

    const reportNowMenuOptionEnabled = React.useMemo(() => {
        if (!enableActions.reportNow || !period.schedule.isComposite || report?.isSubmitted || report?.isFinalized) {
            return false;
        }
        return true;
    }, [enableActions.reportNow, period.schedule.isComposite, report?.isFinalized, report?.isSubmitted]);
    return {
        navigate,
        reportNowMenuOptionEnabled,
    };
}

function ReportNowButton(options: IActionsOptions): JSX.Element | null {
    const { navigate } = useReportActions(options);

    if (options.report?.isSubmitted) return null;

    return (
        <PrimaryButton
            className={classNames("h-min bg-navigator-primary", {
                "pointer-events-none hidden": !options.reportNowButtonEnabled,
            })}
            onClick={() => navigate()}
        >
            Report
        </PrimaryButton>
    );
}

function Menu(options: IActionsOptions): JSX.Element | null {
    const ctx = React.useContext(Context);
    const [isStatementOpen, setIsStatementOpen] = React.useState(false);
    const { navigate, reportNowMenuOptionEnabled } = useReportActions(options);
    const [isLoadingMetadata, setIsLoadingMetadata] = React.useState(false);
    const [metadata, setMetadata] = React.useState<PolicyLayer.Metadata[] | null>(null);
    const getPolicyMetadata = React.useCallback(async () => {
        if (!ctx.getPolicyLayerMetadata) return;
        setIsLoadingMetadata(true);
        const data = await ctx.getPolicyLayerMetadata(options.policy);
        setMetadata(data.reverse());
        setIsLoadingMetadata(false);
        setIsStatementOpen(true);
    }, [ctx, options.policy]);
    const [isPaymentFormOpen, setIsPaymentFormOpen] = React.useState(false);

    const menuOptions: ThreeDotMenu.IOption[] = React.useMemo(() => {
        const o: ThreeDotMenu.IOption[] = [
            {
                id: "viewStatement",
                label: "View Statement",
                onClick: getPolicyMetadata,
                isLoading: isLoadingMetadata,
                disabled: !ctx.getPolicyLayerMetadata || isLoadingMetadata || (ctx.waypointVersion && options.period.invoices.length < 1),
            },
            {
                id: "pay",
                label: "Pay Now",
                onClick: () => {
                    setIsPaymentFormOpen(true);
                },
                disabled: !ctx.billingInfo || !ctx.createPayment || reportNowMenuOptionEnabled,
            },
            {
                id: "report",
                label: "Report Now",
                onClick: () => navigate(),
                disabled: !reportNowMenuOptionEnabled,
                hidden: !Policy.isComposite(options.policy),
            },
        ];

        return o;
    }, [
        ctx.billingInfo,
        ctx.createPayment,
        ctx.getPolicyLayerMetadata,
        ctx.waypointVersion,
        getPolicyMetadata,
        isLoadingMetadata,
        navigate,
        options.period.invoices.length,
        options.policy,
        reportNowMenuOptionEnabled,
    ]);

    if (!ctx.enableActions) return null;

    return (
        <>
            <ThreeDotMenu options={menuOptions} />
            <StatementDialog
                open={isStatementOpen}
                onClose={() => setIsStatementOpen(false)}
                policy={options.policy}
                billingPeriod={options.period}
                layerMetadata={metadata}
            />
            {ctx.billingInfo && ctx.createPayment && (
                <PaymentFormDialog
                    open={isPaymentFormOpen}
                    onClose={() => setIsPaymentFormOpen(false)}
                    policy={options.policy}
                    period={options.period}
                    billingInfo={ctx.billingInfo}
                    createPayment={ctx.createPayment}
                    isLoadingBillingInfo={ctx.isLoadingBillingInfo ?? false}
                    onPaymentSuccess={options.onPaymentSuccess}
                />
            )}
        </>
    );
}

function InvoiceList({ policy, billingPeriod, user }: { policy: Policy; billingPeriod: BillingPeriod; user?: User }): JSX.Element {
    const showDueDates = shouldShowDueDates(policy);
    const data = React.useMemo(() => {
        const all: {
            id: string;
            chargeId?: string;
            number: string;
            charged: string;
            note: string;
            paid: string;
            dueDate: string;
            reconciled: boolean;
        }[] = [];

        return billingPeriod.getMergedChargesAndInvoices().map((m) => {
            return {
                id: m.id,
                chargeId: m.chargeId,
                number: m.invoiceNumber,
                charged: NumberFormatter.Currency.format(m.charged || 0),
                paid: NumberFormatter.Currency.format(m.paid || 0),
                dueDate: m.dueDate ? new DateTime(m.dueDate).format("Mmmm dd, YYYY") : "none",
                reconciled: m.reconciled,
            };
        });
        return all;
    }, [billingPeriod]);

    if (!data?.length) {
        return <p className="px-12">No payments associated with the pay period</p>;
    }
    return (
        <div className="flex w-full flex-col gap-2 px-12">
            {data.map((d, i) => (
                <div
                    key={i}
                    className={classNames("grid items-center gap-4", {
                        "grid-cols-[max-content_1fr_max-content_min-content]": showDueDates,
                        "grid-cols-[max-content_1fr_min-content]": !showDueDates,
                    })}
                >
                    <Tooltip title={d.chargeId ? `Stripe ID: ${d.chargeId}` : ""} underline>
                        <p className="font-mono">#{d.number}</p>
                    </Tooltip>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <p className="">
                                Charged: {d.charged} / Paid: {d.paid}
                            </p>
                            {d.reconciled && (
                                <Tooltip title="Reconciled" underline="none">
                                    <CheckCircleIcon className="relative top-[-1px] h-4 w-4 text-green-500" />
                                </Tooltip>
                            )}
                        </div>
                        <SpecialPaymentAttribute policy={policy} invoiceId={d.id} />
                    </div>

                    {showDueDates && <p className="text-right">{d.dueDate}</p>}

                    {user?.isDeveloper && (
                        <ThreeDotMenu
                            options={[
                                {
                                    id: "copyInvoiceId",
                                    label: "Copy Invoice ID",
                                    onClick: () => {
                                        if (!d.chargeId) return;

                                        navigator.clipboard
                                            .writeText(d.number)
                                            .then(() => {
                                                useSnackbar.add("Copied to clipboard", { variant: "success" });
                                            })
                                            .catch(() => {
                                                useSnackbar.add("Could not copy. Please try again.", {
                                                    variant: "warning",
                                                });
                                            });
                                    },
                                    hidden: isNaN(parseInt(d.number)),
                                },
                                {
                                    id: "copyStripeId",
                                    label: "Copy Stripe ID",
                                    onClick: () => {
                                        if (!d.chargeId) return;

                                        navigator.clipboard
                                            .writeText(d.chargeId)
                                            .then(() => {
                                                useSnackbar.add("Copied to clipboard", { variant: "success" });
                                            })
                                            .catch(() => {
                                                useSnackbar.add("Could not copy. Please try again.", {
                                                    variant: "warning",
                                                });
                                            });
                                    },
                                    hidden: !d.chargeId,
                                },
                            ]}
                        ></ThreeDotMenu>
                    )}
                </div>
            ))}
        </div>
    );
}

function SpecialPaymentAttribute({ policy, invoiceId }: { policy: Policy; invoiceId?: string }): JSX.Element | null {
    const showSpecialPaymentAttribute = React.useMemo(() => {
        if (!policy.billing?.specialPayment) return false;
        return policy.billing?.specialPayment?.amount > 0 && invoiceId && policy.billing.specialPayment.invoiceId.includes(invoiceId);
    }, [policy, invoiceId]);

    if (!showSpecialPaymentAttribute) return null;

    return (
        <div
            className={classNames("flex w-fit items-center justify-center rounded-full border  px-4", {
                "text-danger border-red-400": policy.billing?.specialPayment?.isPaid === false,
                "border-gray-400 text-gray-400": policy.billing?.specialPayment?.isPaid === true,
            })}
        >
            <span className="text-center">{policy.billing?.specialPayment?.isDownPayment ? "Down Payment" : "Escrow Deposit"}</span>
        </div>
    );
}

function PayPlanDueDate({ policy, BillingPeriod }: { policy: Policy; BillingPeriod: BillingPeriod }): JSX.Element {
    const date = React.useMemo(() => BillingPeriod.dueDate.format("mm/dd/YY"), [BillingPeriod.dueDate]);
    const isCompositePolicy = React.useMemo(() => Policy.isComposite(policy), [policy]);

    if (!isCompositePolicy) {
        return <p className="text-right">{date}</p>;
    }

    return (
        <div className="flex justify-end">
            <p className="text-right">{date}</p>
        </div>
    );
}

function PaymentFormDialog({
    open,
    onClose,
    policy,
    period,
    billingInfo,
    createPayment,
    isLoadingBillingInfo,
    onPaymentSuccess,
}: Required<Pick<IContext, "billingInfo" | "isLoadingBillingInfo" | "createPayment" | "onPaymentSuccess">> & {
    open: boolean;
    onClose: () => void;
    policy: Policy;
    period: BillingPeriod;
}): JSX.Element {
    const totalDue = React.useMemo(() => period.getDue().amount, [period]);
    const paymentType = React.useMemo(() => {
        if (
            period.schedule.isComposite &&
            period.index === 0 &&
            period.schedule.specialPayment.isEscrowDeposit &&
            !period.schedule.specialPayment.isPaid
        ) {
            return PaymentIntentMetadata.PaymentType.escrowDeposit;
        }
        if (
            !period.schedule.isComposite &&
            period.index === 0 &&
            period.schedule.specialPayment.isDownPayment &&
            !period.schedule.specialPayment.isPaid
        ) {
            return PaymentIntentMetadata.PaymentType.downPayment;
        }

        return PaymentIntentMetadata.PaymentType.premium;
    }, [
        period.index,
        period.schedule.isComposite,
        period.schedule.specialPayment.isDownPayment,
        period.schedule.specialPayment.isEscrowDeposit,
        period.schedule.specialPayment.isPaid,
    ]);

    return (
        <Dialog open={open} onClose={onClose}>
            <PaymentForm
                billingInfo={billingInfo}
                isLoadingBillingInfo={isLoadingBillingInfo ?? false}
                createPayment={createPayment}
                onSuccess={async () => {
                    await onPaymentSuccess({ policyId: policy.id });
                    onClose();
                }}
                metadata={{
                    [PaymentIntentMetadata.policyId]: policy.id,
                    [PaymentIntentMetadata.billingPeriodIndex]: period.index.toString(),
                    [PaymentIntentMetadata.billingPeriodStartDate]: period.startDate.format("YYYY-MM-DD"),
                    [PaymentIntentMetadata.billingPeriodEndDate]: period.endDate.format("YYYY-MM-DD"),
                    [PaymentIntentMetadata.billingPeriodDueDate]: period.dueDate.format("YYYY-MM-DD"),
                    [PaymentIntentMetadata.paymentType]: paymentType,
                }}
                defaultValues={{
                    description: `Policy: ${policy.number} (Pay Period #${period.index + 1}, ${paymentType})`,
                    invoiceNumber: `${period.startDate.format("MM/DD/YYYY")} - ${period.endDate.format("MM/DD/YYYY")}`,
                    paymentMethodId: policy.billingSettings?.paymentMethodId,
                    items: [
                        {
                            amount: NumberFormatter.round(totalDue, 2),
                            description: capitalizeFirst(paymentType.replaceAll("_", " ")),
                            policyNumber: policy.number,
                        },
                    ],
                }}
            />
        </Dialog>
    );
}
